import { useState } from "react"
import { createFileRoute, Link, useParams } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { AlertTriangle, Bell, Mail, Pencil, Plus, Smartphone, Trash2 } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import {
  AUDIENCE_LABEL,
  createEventReminderRule,
  deleteReminderRule,
  describeOffset,
  eventRemindersKey,
  eventRemindersQueryOptions,
  joinOffset,
  splitOffset,
  updateReminderRule,
  type ReminderAnchor,
  type ReminderAudience,
  type ReminderChannel,
  type ReminderRuleView,
  type ReminderTimelineEntry,
} from "@/core/reminders"
import {
  Card,
  Field,
  GhostButton,
  Pill,
  PrimaryButton,
  SectionPage,
  Select,
  Textarea,
  TextInput,
} from "@/components/settings/primitives"
import { ConfirmDialog } from "@/components/tickets/ConfirmDialog"

// Event-level reminder rules — the "confirm your spot" cadence every published
// event is seeded with (2 / 7 / 14 days after each guest's invite) plus any the
// organizer adds, timed either after the invite or before the event start.
// List, add, edit, switch on/off, delete, and the upcoming send timeline. All
// reads/writes are reminders actions (core/reminders.ts); send times are
// resolved server-side (start-anchored ones in the event's timezone). Session-level
// reminders are listed read-only here (they're managed on the session).

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/reminders")({
  component: RemindersSettings,
})

const DEFAULT_CONFIRM_SUBJECT = "Don't forget to confirm your spot — {event}"

type Draft = {
  anchor: ReminderAnchor
  days: string
  hours: string
  audience: ReminderAudience
  channel: ReminderChannel
  subject: string
  body: string
}

const EMPTY_DRAFT: Draft = {
  anchor: "invite",
  days: "3",
  hours: "0",
  audience: "unconfirmed_guests",
  channel: "email",
  subject: "",
  body: "",
}

function fmtWhen(iso: string | null): string {
  if (!iso) return "—"
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return "—"
  return new Date(t).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function RemindersSettings() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/settings/reminders" })
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const q = useQuery({ ...eventRemindersQueryOptions(eventId ?? ""), enabled: !!eventId })
  const data = q.data
  const [editing, setEditing] = useState<string | "new" | null>(null)
  const [pendingDelete, setPendingDelete] = useState<{ rule: ReminderRuleView; wouldCancel: number } | null>(null)

  const invalidate = () => {
    if (eventId) void qc.invalidateQueries({ queryKey: eventRemindersKey(eventId) })
    void qc.invalidateQueries({ queryKey: ["reminder-rules"] })
  }
  const onError = (e: unknown) =>
    toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong")

  const toggle = useMutation({
    mutationFn: (r: ReminderRuleView) => updateReminderRule(r.id, { active: r.active ? 0 : 1 }),
    onSuccess: (_d, r) => {
      toast.success(r.active ? "Reminder switched off" : "Reminder switched on")
      invalidate()
    },
    onError,
  })
  const askDelete = useMutation({
    mutationFn: async (r: ReminderRuleView) => {
      const preview = await deleteReminderRule(r.id, true)
      return { rule: r, wouldCancel: preview.wouldCancel ?? 0 }
    },
    onSuccess: (v) => setPendingDelete(v),
    onError,
  })
  const del = useMutation({
    mutationFn: (id: string) => deleteReminderRule(id),
    onSuccess: () => {
      toast.success("Reminder deleted")
      setPendingDelete(null)
      invalidate()
    },
    onError,
  })

  const isEventLevel = (r: ReminderRuleView) => r.scopeKind === "event" || r.scopeKind === "invite"
  const eventRules = (data?.rules ?? []).filter(isEventLevel)
  const sessionRules = (data?.rules ?? []).filter((r) => !isEventLevel(r))
  // Only start-anchored (and session) reminders need the venue time zone;
  // invite-anchored ones run off each guest's invite time.
  const needsTimezone = data ? !data.timezone.valid && (data.rules ?? []).some((r) => r.active && r.scopeKind !== "invite") : false
  const ruleById = new Map((data?.rules ?? []).map((r) => [r.id, r]))
  const now = Date.now()
  // Invite-anchored sends have one time per guest, so fold entries for the
  // same reminder in the same minute into one line.
  const upcoming = Object.values(
    (data?.timeline ?? [])
      .filter((t) => (t.status === "pending" || t.status === "claimed") && Date.parse(t.dispatchAt) >= now - 3600_000)
      .reduce<Record<string, ReminderTimelineEntry>>((acc, t) => {
        const k = `${t.ruleId}:${t.dispatchAt.slice(0, 16)}`
        const prev = acc[k]
        acc[k] = prev ? { ...prev, recipients: prev.recipients + t.recipients } : { ...t }
        return acc
      }, {}),
  )
  const recent = (data?.timeline ?? [])
    .filter((t) => !(t.status === "pending" || t.status === "claimed"))
    .slice(-12)
    .reverse()

  return (
    <SectionPage
      title="Reminders"
      description="Scheduled emails and notifications for your guests. Every event starts with three “confirm your spot” nudges to guests who haven't confirmed — 2, 7 and 14 days after their invite was sent. Edit, switch off, or add your own."
      actions={
        <PrimaryButton type="button" onClick={() => setEditing("new")} disabled={!data || editing === "new"}>
          <Plus className="h-4 w-4" />
          Add reminder
        </PrimaryButton>
      }
    >
      {needsTimezone && (
        <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12.5px] leading-relaxed text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong className="font-semibold">Set the event's time zone to schedule reminders before the start.</strong>{" "}
            Those send times are worked out from the event start in the venue's local time, so they
            aren't sent until a time zone is set. Reminders after the invite aren't affected. Your
            reminders are saved and start scheduling as soon as you add one in{" "}
            <Link
              to="/$orgSlug/$eventSlug/settings/details"
              params={{ orgSlug: params.orgSlug, eventSlug: params.eventSlug }}
              className="font-semibold underline"
            >
              Event details
            </Link>
            .
          </div>
        </div>
      )}

      {editing === "new" && eventId && (
        <RuleEditor
          eventId={eventId}
          onDone={() => {
            setEditing(null)
            invalidate()
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      <Card
        title="Event reminders"
        description={
          data
            ? `${data.audienceSize.unconfirmed_guests} ${data.audienceSize.unconfirmed_guests === 1 ? "guest hasn't" : "guests haven't"} confirmed yet · ${data.audienceSize.confirmed_guests} confirmed`
            : undefined
        }
      >
        {q.isLoading ? (
          <p className="text-[12.5px] text-plum-700/55 dark:text-plum-200/40">Loading…</p>
        ) : q.isError ? (
          <p className="text-[12.5px] text-rose-600 dark:text-rose-400">Couldn't load reminders.</p>
        ) : eventRules.length === 0 ? (
          <p className="text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
            No event reminders. Add one to nudge guests after their invite or before the event.
          </p>
        ) : (
          <ul className="space-y-2">
            {eventRules.map((r) =>
              editing === r.id && eventId ? (
                <li key={r.id}>
                  <RuleEditor
                    eventId={eventId}
                    rule={r}
                    onDone={() => {
                      setEditing(null)
                      invalidate()
                    }}
                    onCancel={() => setEditing(null)}
                  />
                </li>
              ) : (
                <li
                  key={r.id}
                  className="flex items-start gap-3 rounded-xl border border-plum-200/60 bg-white px-3.5 py-3 dark:border-white/10 dark:bg-white/[0.02]"
                >
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-plum-100/70 text-plum-700/70 dark:bg-white/[0.06] dark:text-plum-200/60">
                    {r.channel === "push" ? <Smartphone className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                        {describeOffset(r.offsetMinutes, r.scopeKind)}
                      </span>
                      {!r.active && <Pill>Off</Pill>}
                      {r.active && r.isPast && <Pill>Sent / past</Pill>}
                    </div>
                    <div className="mt-0.5 text-[12px] text-plum-700/60 dark:text-plum-200/45">
                      {AUDIENCE_LABEL[r.audience] ?? r.audience}
                      {r.audienceSize != null ? ` (${r.audienceSize} now)` : ""} ·{" "}
                      {r.scopeKind === "invite"
                        ? "Timed per guest"
                        : r.sendsAt
                          ? fmtWhen(r.sendsAt)
                          : data?.timezone.valid
                            ? "—"
                            : "Needs a time zone"}
                    </div>
                    <div className="mt-0.5 truncate text-[12px] text-plum-700/50 dark:text-plum-200/40">
                      Subject: {r.subjectTemplate || (r.audience === "unconfirmed_guests" ? "Event default (Invites & emails)" : "—")}
                    </div>
                    {Object.keys(r.counts).length > 0 && (
                      <div className="mt-1 text-[11.5px] text-plum-700/50 tabular-nums dark:text-plum-200/40">
                        {Object.entries(r.counts)
                          .map(([s, n]) => `${n} ${s}`)
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={r.active}
                      title={r.active ? "Switch off" : "Switch on"}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate(r)}
                      className={`relative h-5 w-9 rounded-full transition disabled:opacity-50 ${r.active ? "bg-[var(--accent)]" : "bg-plum-200 dark:bg-white/15"}`}
                    >
                      <span
                        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${r.active ? "left-[18px]" : "left-0.5"}`}
                      />
                    </button>
                    <button
                      type="button"
                      title="Edit"
                      onClick={() => setEditing(r.id)}
                      className="grid h-8 w-8 place-items-center rounded-lg text-plum-700/60 transition hover:bg-plum-100/60 dark:text-plum-200/50 dark:hover:bg-white/5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      disabled={askDelete.isPending}
                      onClick={() => askDelete.mutate(r)}
                      className="grid h-8 w-8 place-items-center rounded-lg text-plum-700/60 transition hover:bg-rose-50 hover:text-rose-600 dark:text-plum-200/50 dark:hover:bg-rose-500/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ),
            )}
          </ul>
        )}
      </Card>

      <Card
        title="Upcoming sends"
        description={
          data
            ? `Recipients are queued up to ${data.horizonDays} days ahead of each send — guests added later are picked up automatically.`
            : undefined
        }
      >
        {upcoming.length === 0 ? (
          <p className="text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
            {needsTimezone && !eventRules.some((r) => r.active && r.scopeKind === "invite")
              ? "Nothing scheduled — the event has no time zone yet."
              : `Nothing queued in the next ${data?.horizonDays ?? 30} days.`}
          </p>
        ) : (
          <ul className="divide-y divide-plum-100 dark:divide-white/5">
            {upcoming.map((t) => {
              const rule = ruleById.get(t.ruleId)
              return (
                <li key={`${t.ruleId}:${t.dispatchAt}:${t.status}`} className="flex items-center gap-3 py-2 text-[12.5px]">
                  <Bell className="h-3.5 w-3.5 shrink-0 text-plum-700/45 dark:text-plum-200/35" />
                  <span className="w-44 shrink-0 font-medium text-plum-900 tabular-nums dark:text-plum-50">
                    {fmtWhen(t.dispatchAt)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-plum-700/65 dark:text-plum-200/50">
                    {rule ? `${describeOffset(rule.offsetMinutes, rule.scopeKind)} · ${AUDIENCE_LABEL[rule.audience] ?? rule.audience}` : "Reminder"}
                  </span>
                  <span className="shrink-0 text-plum-700/60 tabular-nums dark:text-plum-200/45">
                    {t.recipients} {t.recipients === 1 ? "guest" : "guests"}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
        {recent.length > 0 && (
          <div className="border-t border-plum-100 pt-3 dark:border-white/5">
            <div className="mb-1 text-[11px] font-semibold tracking-wide text-plum-700/45 uppercase dark:text-plum-200/35">
              Recent
            </div>
            <ul className="space-y-1 text-[12px] text-plum-700/60 dark:text-plum-200/45">
              {recent.map((t) => (
                <li key={`${t.ruleId}:${t.dispatchAt}:${t.status}`} className="tabular-nums">
                  {fmtWhen(t.dispatchAt)} — {t.recipients} {t.status}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {sessionRules.length > 0 && (
        <Card title="Session reminders" description="Set on individual sessions in the schedule.">
          <ul className="space-y-1 text-[12.5px] text-plum-700/65 dark:text-plum-200/50">
            {sessionRules.map((r) => (
              <li key={r.id}>
                {describeOffset(r.offsetMinutes)} · {r.channel} · {AUDIENCE_LABEL[r.audience] ?? r.audience}
                {!r.active && " · off"}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete reminder?"
        body={
          pendingDelete ? (
            <>
              <span className="font-semibold">
                {describeOffset(pendingDelete.rule.offsetMinutes, pendingDelete.rule.scopeKind)}
              </span>{" "}
              will be removed
              {pendingDelete.wouldCancel > 0
                ? ` and ${pendingDelete.wouldCancel} queued ${pendingDelete.wouldCancel === 1 ? "email" : "emails"} won't be sent`
                : ""}
              . Deleted default reminders are not re-added. To pause it instead, switch it off.
            </>
          ) : null
        }
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        icon={<Trash2 className="h-4 w-4" />}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.rule.id)}
      />
    </SectionPage>
  )
}

function RuleEditor({
  eventId,
  rule,
  onDone,
  onCancel,
}: {
  eventId: string
  rule?: ReminderRuleView
  onDone: () => void
  onCancel: () => void
}) {
  const ruleAnchor: ReminderAnchor = rule?.scopeKind === "event" ? "event" : "invite"
  const seed = rule ? splitOffset(rule.offsetMinutes, ruleAnchor) : null
  const [d, setD] = useState<Draft>(() =>
    rule
      ? {
          anchor: ruleAnchor,
          days: String(seed!.days),
          hours: String(seed!.hours),
          audience: (rule.audience === "confirmed_guests" ? "confirmed_guests" : "unconfirmed_guests") as ReminderAudience,
          channel: (rule.channel === "push" ? "push" : "email") as ReminderChannel,
          subject: rule.subjectTemplate ?? "",
          body: "",
        }
      : EMPTY_DRAFT,
  )
  const days = Number(d.days)
  const hours = Number(d.hours)
  const offsetOk = Number.isFinite(days) && Number.isFinite(hours) && days >= 0 && hours >= 0 && hours < 24 && days <= 365
  // The unconfirmed audience gets the built-in "confirm your spot" email; any
  // other audience needs its own message (and push needs a title).
  const needsBody = d.audience !== "unconfirmed_guests"
  const valid = offsetOk && (!needsBody || d.body.trim().length > 0 || !!rule) && (d.channel === "email" || d.subject.trim().length > 0)

  const save = useMutation({
    mutationFn: () => {
      const subject = d.subject.trim() || null
      const base = {
        scopeKind: d.anchor,
        offsetMinutes: joinOffset(days, hours, d.anchor),
        channel: d.channel,
        audience: d.audience,
        subjectTemplate: subject,
        ...(d.body.trim() ? { bodyTemplate: d.body.trim() } : {}),
      }
      return rule ? updateReminderRule(rule.id, base) : createEventReminderRule(eventId, base)
    },
    onSuccess: () => {
      toast.success(rule ? "Reminder updated" : "Reminder added")
      onDone()
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't save the reminder"),
  })

  return (
    <Card title={rule ? "Edit reminder" : "New reminder"}>
      <Field
        label="When"
        hint={
          d.anchor === "invite"
            ? "Counted from when each guest's invite was sent, so guests invited later get the same timing. Nothing is sent after the event starts."
            : "Counted back from the event start, in the event's time zone."
        }
      >
        <Select value={d.anchor} onChange={(e) => setD({ ...d, anchor: e.target.value as ReminderAnchor })}>
          <option value="invite">After the invite is sent</option>
          <option value="event">Before the event starts</option>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={d.anchor === "invite" ? "Days after the invite" : "Days before the start"}>
          <TextInput type="number" min={0} max={365} value={d.days} onChange={(e) => setD({ ...d, days: e.target.value })} />
        </Field>
        <Field label="Plus hours">
          <TextInput type="number" min={0} max={23} value={d.hours} onChange={(e) => setD({ ...d, hours: e.target.value })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Send to">
          <Select value={d.audience} onChange={(e) => setD({ ...d, audience: e.target.value as ReminderAudience })}>
            <option value="unconfirmed_guests">{AUDIENCE_LABEL.unconfirmed_guests}</option>
            <option value="confirmed_guests">{AUDIENCE_LABEL.confirmed_guests}</option>
          </Select>
        </Field>
        <Field label="Channel">
          <Select value={d.channel} onChange={(e) => setD({ ...d, channel: e.target.value as ReminderChannel })}>
            <option value="email">Email</option>
            <option value="push">Push notification</option>
          </Select>
        </Field>
      </div>
      <Field
        label={d.channel === "push" ? "Notification title" : "Subject (optional)"}
        hint={
          d.audience === "unconfirmed_guests" && d.channel === "email"
            ? `Leave blank to use the event's confirm-reminder subject (Invites & emails) — default “${DEFAULT_CONFIRM_SUBJECT}”. {event} fills in the event name.`
            : "{event} fills in the event name."
        }
      >
        <TextInput maxLength={300} value={d.subject} onChange={(e) => setD({ ...d, subject: e.target.value })} />
      </Field>
      {needsBody && (
        <Field
          label="Message"
          hint={rule ? "Leave blank to keep the current message." : "The text guests receive. {{event.name}} fills in the event name."}
        >
          <Textarea rows={4} maxLength={20000} value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} />
        </Field>
      )}
      {d.audience === "unconfirmed_guests" && (
        <p className="text-[12px] leading-relaxed text-plum-700/55 dark:text-plum-200/40">
          Guests who haven't confirmed get the “confirm your spot” email with their personal link. Anyone who
          confirms before it goes out is skipped automatically.
        </p>
      )}
      {!offsetOk && (
        <p className="text-[12px] text-rose-600 dark:text-rose-400">Use whole days (0–365) and hours (0–23).</p>
      )}
      <div className="flex justify-end gap-2">
        <GhostButton type="button" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </GhostButton>
        <PrimaryButton type="button" onClick={() => save.mutate()} disabled={!valid || save.isPending}>
          {save.isPending ? "Saving…" : rule ? "Save reminder" : "Add reminder"}
        </PrimaryButton>
      </div>
    </Card>
  )
}
